import type { ReactNode } from 'react';
import { HandCoins, Wallet } from 'lucide-react';
import { inr2 } from '../../lib/format';
import { formatIstDate } from '../../lib/time';

/** `moneyPicture` on GET /expense-reports/:id — the rep's money position behind an open claim. */
export interface MoneyPicture {
  /** The wallet balance exactly as the rep's wallet shows it. */
  walletBalance: number;
  /** Already taken off the wallet for THIS claim (when it was submitted / approved) — inside walletBalance. */
  postedForClaim?: number;
  postedTxnNo?: string | null;
  postedAt?: string | null;
  floatHeld: number;
  openClaimsAhead: { count: number; amount: number };
  /** Settlement arithmetic (FIFO) — what the wallet can still carry for this claim. Never shown as a balance. */
  floatForThisClaim: number;
  claimAmount: number;
  metFromFloat: number;
  toPay: number;
  floatLeftAfter: number;
  fundingSource: string;
  collection: {
    collected: number; banked: number; spentOnClaims: number;
    awaitingBankConfirmation: number; inHand: number; outstanding: number;
  };
}

function Row({ label, value, strong, tone }: { label: ReactNode; value: ReactNode; strong?: boolean; tone?: 'warn' | 'danger' }) {
  const ink = tone === 'warn' ? 'text-warning-600 dark:text-harvest-300' : tone === 'danger' ? 'text-danger-600 dark:text-danger-500' : 'text-content';
  return (
    <div className={`flex justify-between gap-4 ${strong ? 'border-t border-line pt-1' : ''}`}>
      <dt className={strong ? 'font-medium text-content' : 'text-muted'}>{label}</dt>
      <dd className={`tabular-nums ${strong ? 'font-semibold' : ''} ${ink}`}>{value}</dd>
    </div>
  );
}

/**
 * WHAT A DECISION DOES TO THE REP'S MONEY — one calculation for every screen that states it.
 *
 * Every figure a reader is shown is one they can find elsewhere: the wallet balance is the balance their
 * wallet page shows, NOT "balance + this claim". (A claim debits the wallet when it is submitted, so the
 * ₹192 of a ₹192 claim is already out of a ₹1 balance — adding it back produced a "₹193 float" the rep
 * never had.)
 *
 *   walletShare   the part of the accepted amount that falls on the wallet (all of it, none if it came
 *                 out of retailer cash, or what the cash part leaves)
 *   toPay         what Accounts pays them — only what the wallet cannot carry (settlement FIFO)
 *   walletAfter   balance now + what this claim already took − what it should take + any payout
 *   cashLeft      retailer cash they must still hand over
 */
export function computeClaimOutcome(p: MoneyPicture, o: { amount?: number | null; fundingSource?: string | null; cashPart?: number | null } = {}) {
  const r2 = (x: number) => Math.round(x * 100) / 100;
  const source = String(o.fundingSource || p.fundingSource || 'WALLET').toUpperCase();
  const claim = o.amount != null && Number.isFinite(Number(o.amount)) ? Math.max(0, Number(o.amount)) : p.claimAmount;
  const cashAsked = source === 'CASH_CUSTODY' ? claim : source === 'SPLIT' ? Math.max(0, Number(o.cashPart ?? 0)) : 0;
  const fromCash = Math.min(claim, cashAsked);
  const walletShare = r2(claim - fromCash);
  const toPay = Math.max(0, r2(walletShare - p.floatForThisClaim));
  const posted = Number(p.postedForClaim ?? 0);
  const walletAfter = r2(p.walletBalance + posted - walletShare + toPay);
  const inHand = p.collection.inHand;
  // The same refusals the server applies — said before the click, not after it.
  let problem: string | null = null;
  if (claim > p.claimAmount + 0.005) problem = `More than the ${inr2(p.claimAmount)} claimed — the most you can approve is the claim.`;
  else if (fromCash > inHand + 0.005) problem = `Only ${inr2(inHand)} of retailer cash is in their hands — they cannot have spent ${inr2(fromCash)} of it.`;
  else if (source === 'SPLIT' && !(cashAsked > 0.005)) problem = 'Enter how much of it came from the retailer cash they hold.';
  else if (source === 'SPLIT' && cashAsked >= claim - 0.005) problem = 'The cash part covers the whole amount — choose “Retailer cash they collected” instead.';
  return { source, claim, fromCash, walletShare, toPay, walletAfter, cashLeft: Math.max(0, r2(inHand - fromCash)), problem };
}

/** A balance in words: a negative wallet is money the company owes back, not "−₹300". */
const walletText = (n: number) => (n < -0.005 ? `${inr2(Math.abs(n))} overdrawn` : inr2(n));

/** "If you approve" / "When you settle" — the three consequences a reader asks about, in words, live. */
export function ClaimOutcomeSummary({ picture, repName, amount, fundingSource, cashPart, verb = 'approve' }: {
  picture: MoneyPicture; repName?: string | null; amount?: number | null; fundingSource?: string | null; cashPart?: number | null;
  verb?: 'approve' | 'pay';
}) {
  const o = computeClaimOutcome(picture, { amount, fundingSource, cashPart });
  const who = repName || 'The rep';
  const changed = Math.abs(o.walletAfter - picture.walletBalance) > 0.005;
  // Every wallet movement is named, so no after-figure appears without its reason.
  const putBack = Math.round((Number(picture.postedForClaim ?? 0) - o.walletShare) * 100) / 100;
  const why = [
    putBack > 0.005 ? `${inr2(putBack)} taken off when they submitted is put back` : null,
    putBack < -0.005 ? `${inr2(-putBack)} more is taken off` : null,
    o.toPay > 0.005 ? `Accounts’ ${inr2(o.toPay)} payment is added` : null,
  ].filter(Boolean).join('; ');
  return (
    <div className={`rounded-card border px-3 py-2.5 text-sm ${o.problem ? 'border-danger-500/40 bg-danger-500/10' : 'border-line bg-surface'}`}>
      <div className="mb-1.5 type-overline text-subtle">
        {verb === 'approve' ? `If you approve ${inr2(o.claim)}` : `When you settle this ${inr2(o.claim)} claim`}
      </div>
      {o.problem ? (
        <div className="font-medium text-danger-600 dark:text-danger-500">{o.problem}</div>
      ) : (
        <ul className="space-y-1">
          <li className="font-medium text-content">
            {o.toPay > 0
              ? <>{who} is paid <span className="text-warning-600 dark:text-harvest-300">{inr2(o.toPay)}</span> by Accounts</>
              : <>{who} is paid nothing</>}
          </li>
          <li className="text-muted">
            Wallet balance: {walletText(picture.walletBalance)}
            {changed ? <> → <span className="text-content">{walletText(o.walletAfter)}</span>{why ? ` (${why})` : ''}</> : ' — no change'}
          </li>
          <li className="text-muted">
            Retailer cash they must hand over: {inr2(picture.collection.inHand)}
            {o.fromCash > 0 ? <> → <span className="text-content">{inr2(o.cashLeft)}</span></> : ' — no change'}
          </li>
        </ul>
      )}
    </div>
  );
}

/**
 * WHERE THE REP'S MONEY STANDS NOW — only figures their own wallet and money screens also show.
 *
 * What a decision CHANGES is stated once, in `ClaimOutcomeSummary`, beside the choices that make it.
 * (`mode` / `amount` / `fundingSource` / `cashPart` / `positionOnly` are accepted for existing callers and
 * no longer change what is drawn.)
 */
export function ClaimMoneyPicture({ picture }: {
  picture: MoneyPicture; mode?: 'approve' | 'pay'; amount?: number | null;
  fundingSource?: string | null; cashPart?: number | null; positionOnly?: boolean;
}) {
  const p = picture;
  const c = p.collection;
  const posted = Number(p.postedForClaim ?? 0);
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <section className="rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm">
        <h4 className="mb-1.5 flex items-center gap-1.5 type-label text-content"><Wallet size={14} aria-hidden /> Company wallet (float)</h4>
        <dl className="space-y-1">
          <Row label="Wallet balance now" value={walletText(p.walletBalance)} tone={p.walletBalance < -0.005 ? 'danger' : undefined} />
        </dl>
        {posted > 0.005 && (
          <p className="mt-1.5 type-caption text-muted">
            This claim’s {inr2(posted)} was already taken off the wallet when it was submitted
            {p.postedAt ? ` (${formatIstDate(p.postedAt)})` : ''} — the balance above is after it.
          </p>
        )}
        {p.openClaimsAhead.count > 0 && (
          <p className="mt-1 type-caption text-muted">
            {p.openClaimsAhead.count} older {p.openClaimsAhead.count === 1 ? 'claim' : 'claims'} ({inr2(p.openClaimsAhead.amount)}) waiting ahead of this one.
          </p>
        )}
      </section>
      <section className="rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm">
        <h4 className="mb-1.5 flex items-center gap-1.5 type-label text-content"><HandCoins size={14} aria-hidden /> Retailer cash they collected</h4>
        {/* Only what is still theirs to answer for. Banking the bank has confirmed (and cash already
            spent on approved claims) is settled and left out; a deposit the bank has not confirmed yet
            is still their responsibility, so it is shown beside the cash in hand and added to it. */}
        <dl className="space-y-1">
          <Row label="Cash still in their hands" value={inr2(c.inHand)} tone={c.inHand > 0 ? 'warn' : undefined} />
          {c.awaitingBankConfirmation > 0 && (
            <>
              <Row label="Banked, waiting for bank confirmation" value={inr2(c.awaitingBankConfirmation)} />
              <Row strong label="Collected, not yet confirmed in the bank" value={inr2(c.outstanding)} />
            </>
          )}
        </dl>
      </section>
    </div>
  );
}
